// DepartmentTabLogic.js
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Box, Tooltip, IconButton } from "@mui/material";
import Icon from "components/icon";
import {
  getDepartments,
  saveDepartment,
  deleteDepartment,
} from "screens/Employees/logic/Departmentlogic";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useTheme } from "@mui/material/styles";

export const useDepartmentLogic = (toast) => {
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);
  const theme = useTheme();

  // States
  const [loading, setLoading] = useState(false);
  const [openDepartmentDlg, setOpenDepartmentDlg] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rows, setRows] = useState([]);
  const [department, setDepartment] = useState({
    departmentID: 0,
    department: "",
    isActive: true,
  });
  const [existingDepartment, setExistingDepartment] = useState({
    ...department,
  });
  const [departmentContent, setDepartmentContent] = useState({
    entity: "create",
    title: "Create New Department",
    btnText: "Save Department",
  });
  const [errors, setErrors] = useState({
    department: false,
  });
  const [btnDisabled, setBtnDisabled] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  //#region Handlers

  useEffect(() => {
    const isEqual =
      department &&
      existingDepartment &&
      department.department === existingDepartment.department;
    setBtnDisabled(isEqual);
  }, [department, existingDepartment]);

  useEffect(() => {
    fetchDepartments();
  }, []);

  // Filtered rows based on search query
  const filteredRows = useMemo(() => {
    if (!searchQuery) return rows;
    
    const query = searchQuery.toLowerCase();
    return rows.filter(row => 
      row.departmentName?.toLowerCase().includes(query) ||
      row.department?.toLowerCase().includes(query)
    );
  }, [rows, searchQuery]);

  // Search handler
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const fetchDepartments = async () => {
    try {
      setLoading(true);

      const response = await getDepartments(navigate);

      let departmentsData = [];

      if (response && Array.isArray(response.result)) {
        departmentsData = response.result;
      } else if (Array.isArray(response)) {
        departmentsData = response;
      } else {
        departmentsData = [];
      }

      if (departmentsData.length > 0) {
        const formattedData = departmentsData
          .map((dept) => ({
            id: dept.departmentID,
            departmentID: dept.departmentID,
            departmentName: dept.department,
            department: dept.department,
            isActive: dept.isActive,
          }))
          .sort((a, b) => a.departmentName.localeCompare(b.departmentName));

        setRows(formattedData);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
      toast.error("Failed to fetch departments");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDepartmentDlg = () => {
    setDepartmentContent({
      entity: "create",
      title: "Create New Department",
      btnText: "Save Department",
    });
    setDepartment({
      departmentID: 0,
      department: "",
      isActive: true,
    });
    setExistingDepartment({
      departmentID: 0,
      department: "",
      isActive: true,
    });
    setOpenDepartmentDlg(true);
  };

  const handleCloseDepartmentDlg = () => {
    setOpenDepartmentDlg(false);
    resetDepartment();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDepartment((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    const newErrors = {
      department: !department.department,
    };

    setErrors(newErrors);

    if (
      Object.values(newErrors).some((error) => error !== false && error !== "")
    ) {
      toast.error("Please enter department name");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        departmentID: department.departmentID || 0,
        department: department.department,
        isActive:
          department.isActive !== undefined ? department.isActive : true,
      };

      const result = await saveDepartment(payload, navigate);

      if (result && !result.isError) {
        toast.success(
          `Department ${
            departmentContent.entity === "create" ? "created" : "updated"
          } successfully`
        );
        handleCloseDepartmentDlg();
        fetchDepartments();
        resetDepartment();
      } else {
        toast.error(
          result?.message ||
            `Failed to ${
              departmentContent.entity === "create" ? "create" : "update"
            } department`
        );
      }
    } catch (error) {
      console.error("Error saving department:", error);
      toast.error("An error occurred while saving department");
    } finally {
      setLoading(false);
    }
  };

  const resetDepartment = () => {
    setDepartment({
      departmentID: 0,
      department: "",
      isActive: true,
    });
    setErrors({ department: false });
  };

  const showDeleteConfirmation = (id) => {
    MySwal.fire({
      title: "<strong>Are you sure?</strong>",
      html: "<span>You won't be able to revert this!</span>",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it",
      width: "390px",
      padding: "1rem",
      focusConfirm: false,
      showCloseButton: true,
      closeButtonHtml: "&times;",
      customClass: {
        popup:
          theme.palette.mode === "dark"
            ? "custom-swal-popup-dark"
            : "custom-swal-popup",
        title:
          theme.palette.mode === "dark"
            ? "custom-swal-title-dark"
            : "custom-swal-title",
        htmlContainer: "custom-swal-html",
        confirmButton: "custom-swal-confirm",
        cancelButton:
          theme.palette.mode === "dark"
            ? "custom-swal-cancel-dark"
            : "custom-swal-cancel",
        closeButton: "custom-swal-close",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        handleDeleteDepartment(id);
      }
    });
  };

  const handleDeleteDepartment = async (id) => {
    try {
      const result = await deleteDepartment(id, navigate);

      if (result && result.isError) {
        const errorMessage = result.message || "Department failed to delete.";
        if (
          errorMessage.toLowerCase().includes("not found") ||
          errorMessage.toLowerCase().includes("default") ||
          errorMessage.toLowerCase().includes("cannot be deleted")
        ) {
          toast.error("Default departments cannot be deleted", {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
             theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
        } else {
          toast.error(errorMessage, {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
             theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
        }
      } else {
        fetchDepartments();
        toast.success("Department deleted successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    } catch (error) {
      console.error("Error deleting Department:", error);
      toast.error("An error occurred while deleting department");
    }
  };

  const handleDepartmentEdit = (id) => {
    const departmentToEdit = rows.find((dept) => {
      console.log(
        `Checking: ${dept.departmentID} === ${id}`,
        dept.departmentID === id
      );
      return dept.departmentID === id;
    });

    if (departmentToEdit) {
      setDepartmentContent({
        entity: "edit",
        title: "Edit Department",
        btnText: "Save Changes",
      });
      setDepartment({
        departmentID: departmentToEdit.departmentID,
        department: departmentToEdit.department,
        isActive:
          departmentToEdit.isActive !== undefined
            ? departmentToEdit.isActive
            : true,
      });
      setExistingDepartment({
        departmentID: departmentToEdit.departmentID,
        department: departmentToEdit.department,
        isActive:
          departmentToEdit.isActive !== undefined
            ? departmentToEdit.isActive
            : true,
      });

      setOpenDepartmentDlg(true);
    } else {
      toast.error("Department not found");
    }
  };

  //#region Columns Configuration
  const columns = [
    {
      flex: 0.1,
      minWidth: 150,
      field: "departmentName",
      headerName: "Department Name",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.departmentName}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actions",
      minWidth: 180,
      maxWidth: 180,
      headerName: "Action",
      renderCell: (params) => {
        const { row } = params;
        return (
          <>
            <Tooltip title="Edit">
              <IconButton
                onClick={() => handleDepartmentEdit(row.departmentID)}
              >
                <Icon icon="tabler:pencil" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                onClick={() => showDeleteConfirmation(row.departmentID)}
              >
                <Icon icon="tabler:trash" />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
  ];
  //#endregion Columns Configuration

  const DepartmentProps = {
    departmentContent,
    resetDepartment,
    department,
    setDepartment,
    existingDepartment,
    handleChange,
    errors,
    handleSave,
    rows,
    fetchDepartments,
    columns,
    paginationModel,
    setPaginationModel,
    openDepartmentDlg,
    handleOpenDepartmentDlg,
    handleCloseDepartmentDlg,
    btnDisabled,
    loading,
    searchQuery,
    handleSearchChange,
    filteredRows,
  };

  return {
    DepartmentProps,
  };
};